import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRoleLists } from '../shared/slices/rolesSlice';
import { getEnvVar } from '../utils/getEnvVar';
import {
  Navigate,
  useBeforeUnload,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  Box,
  Button,
  Step,
  StepButton,
  StepContent,
  Stepper,
  Typography
} from '@mui/material';
import { useToastProvider } from '../shared/ToastProvider';
import { useUserInfo } from '../shared/UserInfo';
import { useAPIProgress } from '../shared/APIProgress';
import NoAccessEmployee from './NoAccessEmployee';
import {
  checkStrategyDuplicateCombination,
  getNewMGCCriteria
} from './StrategyUtils';
import {
  BEGINNING_PHASE,
  LABELS,
  SBF_US_MESSAGE,
  SCOPE_LEVEL_UNITORUNITGROUP,
  SMALL_BUSINESS_FORM,
  STARTING_PHASE_FOR_ACTIVE_STATUS,
  STRATEGY_STATUS_ACTIVE,
  STRATEGY_STATUS_DELETED,
  STRATEGY_STATUS_DRAFT
} from '../shared/AppConstants';
import { useStatusMessages } from '../shared/StatusMessages';
import MessageBanner from '../shared/MessageBanner';
import { getFormattedUserNameWithId } from '../shared/UserLogin';
import {
  callMasterScopeAPI,
  callMGCMasterAPI,
  callStrategiesGetAPI,
  callUserInfoAPI
} from '../shared/APIHelper';
import {
  checkedMgcs,
  checkedScope,
  findUSIndicatorNew,
  getOwnerObj,
  getUserObj,
  isEventSourceTrusted,
  isScopeAnyGlobalType
} from '../shared/UtilFunctions';
import usePrompt from '../shared/CustomPrompt';
import SMModal from '../shared/SMModal';
import { PageHeader } from '@deere/fuel-react/PageHeader.js';
import { PageHeaderTitle } from '@deere/fuel-react/PageHeaderTitle.js';
import { PageHeaderActions } from '@deere/fuel-react/PageHeaderActions.js';
import { PageContent } from '@deere/fuel-react/PageContent.js';

import { fetchJsonWithAuth } from '../shared/fetch-json.util';
import DefineStrategy from './strategy_stepper/DefineStrategy';
import AddSupplierStep from './strategy_stepper/AddSupplierStep';
import SourcingDiversityFormStep from './strategy_stepper/SourcingDiversityFormStep';
import ReviewPublish from './strategy_stepper/ReviewPublish';
import {
  hydrateScope,
  resetScopeState,
  selectScopeDirty,
  selectScopeType,
  selectSelectedScopeUnits,
  selectSelectedScopeViewList,
  setScopeDirty as setScopeDirtyInState,
  setScopeInvalidError as setScopeInvalidErrorInState,
  setSelectedScopeViewList as setSelectedScopeViewListInState
} from './Strategy/scopeSlice';
import { useScopeValidation } from './Strategy/useScopeValidation';

const STEPS = {
  DEFINE: 0,
  SUPPLIER: 1,
  SDF: 2,
  REVIEW: 2,
  MAX: 2
};

const STEPS_LINK = {
  DEFINE: 'defineSection',
  SUPPLIER: 'supplierSection',
  SDF: 'sdfSection',
  REVIEW: 'reviewSection'
};

export default function StrategyFormStepper() {
  const info = useUserInfo();
  const { enqueueToast } = useToastProvider();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    strategyAdmins,
    sroSBMs,
    sroCommMgrs,
    status: rolesStatus
  } = useSelector((state) => state.roles);
  const rolesProgressTracked = useRef(false);
  const loggedInUser = info?.userId;
  const loggedInUserName = info?.userName;
  const tokenAvailable = info?.token;
  const isAdmin = info?.isAdmin;
  const hasCreateAccess = info?.hasCreateAccess;
  const [hasAccess, setHasAccess] = useState(null);
  const [readOnly] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [statusState, setStatusState] = useState('');
  const [strategyInformation, setStrategyInformation] = useState(null);
  const [lastUpdatedBy, setLastUpdatedBy] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [makeCancelAsLink, setMakeCancelAsLink] = useState(false);
  const handleModalCancel = () => setShowModal(false);
  const handleModalDeleteCancel = () => setShowDeleteModal(false);
  const cancelOKStrategy = () => {
    setMakeCancelAsLink(true);
  };
  const [userInfoMap, setUserInfoMap] = useState(null);
  const [owners, setOwners] = useState(null);
  const [approvers, setApprovers] = useState(null);
  const [ownerObj, setOwnerObj] = useState(null);
  const [masterMGCJson, setMasterMGCJson] = useState(null);
  const [masterScopeJson, setMasterScopeJson] = useState(null);
  const [scopeUSIndicatorMap, setScopeUSIndicatorMap] = useState(null);
  const [selectedMgcsUpdated, setSelectedMgcsUpdated] = useState(false);
  const [selectedMgc, setSelectedMgc] = useState(null);
  const [mgcDirty, setMgcDirty] = useState(false);
  const [selectedMgcList, setSelectedMgcList] = useState(null);
  const [selectedMgcAfterEdit, setSelectedMgcAfterEdit] = useState();
  const [duplicateStrategyList, setDuplicateStrategyList] = useState(null);
  const [okToPublish, setOkToPublish] = useState(true);
  const [hasUSScope, setHasUSScope] = useState(null);
  const [hasSDFStep, setHasSDFStep] = useState(false);
  const [selectedOverviewViewList, setSelectedOverviewViewList] =
    useState(null);
  const [supplierScopeOverview, setSupplierScopeOverview] = useState(null);
  const [selectedMgcOnLoad, setSelectedMgcOnLoad] = useState();
  const [strategySave, setStrategySave] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [saveMgcError, setSaveMgcError] = useState(false);
  const [missingScopeError, setMissingScopeError] = useState(false);

  const [suppliersDirty, setSuppliersDirty] = useState(false);
  const [selectedSuppliers, setSelectedSuppliers] = useState(null);
  const [strategySupplierUsers, setStrategySupplierUsers] = useState(null);
  const [nonStrategicSuppliers, setNonStrategicSuppliers] = useState(null);

  const { apiInProgressCount, incrementAPIInProgress, decrementAPIInProgress } =
    useAPIProgress();

  const { strategyId } = useParams();

  let Location = useLocation();
  let id = 0;

  if (strategyId) {
    id = parseInt(strategyId);
  } else if (Location.state?.strategyId) {
    id = Location.state.strategyId;
  }

  const [strategyNumber, setStrategyNumber] = useState(id);

  const selectedScopeUnits = useSelector(selectSelectedScopeUnits);
  const selectedScopeViewList = useSelector(selectSelectedScopeViewList);
  const scopeType = useSelector(selectScopeType);
  const scopeDirty = useSelector(selectScopeDirty);

  const { statusMessages, setStatusErrorMsg, clearAllMessages } =
    useStatusMessages();
  const {
    register,
    reset,
    control,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors, isDirty },
    watch
  } = useForm({
    mode: 'all',
    defaultValues: { scope: null }
  });
  const watchFields = watch(['approverId', 'ownerId', 'strategyDesc']);

  const commonProps = {
    tokenAvailable: tokenAvailable,
    setStatusErrorMsg: setStatusErrorMsg,
    register: register,
    incrementAPIInProgress: incrementAPIInProgress,
    decrementAPIInProgress: decrementAPIInProgress,
    strategyNumber: strategyNumber,
    errors: errors,
    control: control,
    isEdit: isEdit,
    watchFields: watchFields,
    masterScopeJson: masterScopeJson,
    reset: reset,
    setValue: setValue
  };

  const [activeStep, setActiveStep] = useState(STEPS.DEFINE);

  const strategyProps = {
    owners: owners,
    approvers: approvers,
    ownerObj: ownerObj,
    userInfoMap: userInfoMap,
    setParentMgcData: setParentMgcData,
    selectedMgc: selectedMgc,
    strategyInformation: strategyInformation,
    masterMGCJson: masterMGCJson,
    selectedMGCList: selectedMgcList,
    selectedMgcAfterEdit: selectedMgcAfterEdit,
    resetSelectedMgcAfterEdit: resetSelectedMgcAfterEdit,
    duplicateStrategyList: duplicateStrategyList,
    okToPublish: okToPublish,
    hasUSScope: hasUSScope,
    readOnly: readOnly,
    statusMessages: statusMessages,
    selectedMgcsUpdated: selectedMgcsUpdated,
    masterScopeJson: masterScopeJson,
    selectedScopeUnits: selectedScopeUnits,
    scopeType: scopeType,
    saveMgcError: saveMgcError,
    missingScopeError: missingScopeError,
    selectedScopeViewList: selectedScopeViewList,
    setSuppliersDirty: setSuppliersDirty,
    setParentSupplierData: setParentSupplierData,
    setParentNonStrategicSupplierData: setParentNonStrategicSupplierData,
    supplierScopeOverview: supplierScopeOverview,
    selectedOverviewViewList: selectedOverviewViewList,
    setSelectedOverviewViewList: setSelectedOverviewViewList
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) =>
      prevActiveStep < getNextStepNumberWrtSDFStep(STEPS.MAX)
        ? prevActiveStep + 1
        : prevActiveStep
    );
  };

  useEffect(() => {
    if (tokenAvailable) {
      setOwnerObj(getOwnerObj(loggedInUser, loggedInUserName));

      dispatch(fetchRoleLists());

      callMGCMasterAPI(
        setMasterMGCJson,
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        true
      );
      callMasterScopeAPI(
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        setMasterScopeJson,
        setScopeUSIndicatorMap
      );
    }
  }, [tokenAvailable, dispatch]);

  useEffect(() => {
    dispatch(resetScopeState());
    return () => {
      dispatch(resetScopeState());
    };
  }, [dispatch]);

  useEffect(() => {
    // Only increment once per dispatch cycle — guard against the component
    // mounting after another component already started the fetch.
    if (rolesStatus === 'loading' && !rolesProgressTracked.current) {
      rolesProgressTracked.current = true;
      incrementAPIInProgress();
      return;
    }

    const isDone = rolesStatus === 'loaded' || rolesStatus === 'error';

    if (isDone && rolesProgressTracked.current) {
      rolesProgressTracked.current = false;
      decrementAPIInProgress();
    }

    if (rolesStatus === 'error') {
      setStatusErrorMsg(
        'Failed to load the list of owners and approvers. Please refresh the page or try again.',
        null
      );
    }
  }, [rolesStatus]);

  useEffect(() => {
    if (rolesStatus !== 'loaded') return;

    if (strategyAdmins && sroSBMs && sroCommMgrs) {
      if (strategyNumber > 0 && strategySupplierUsers === null) return;

      // Load Owners and Approver Ids -- names
      let map = new Map();

      strategyAdmins.forEach((element) => {
        map.set(element, element);
      });
      sroSBMs.forEach((element) => {
        map.set(element, element);
      });
      sroCommMgrs.forEach((element) => {
        map.set(element, element);
      });

      if (strategySupplierUsers && strategySupplierUsers.length > 0) {
        strategySupplierUsers.forEach((element) => {
          map.set(element, element);
        });
      }

      const userIds = [...map.values()];

      callUserInfoAPI(
        userIds,
        setUserInfoMap,
        null,
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        true
      );
    }
  }, [
    rolesStatus,
    strategyAdmins,
    sroSBMs,
    sroCommMgrs,
    strategySupplierUsers,
    strategyNumber
  ]);

  useEffect(() => {
    if (userInfoMap) {
      // Load Owners and Approver User Info - complete
      let ownerList = [];
      let approverList = [];

      strategyAdmins.forEach((element) => {
        ownerList.push(userInfoMap.get(element));
        approverList.push(userInfoMap.get(element));
      });
      sroSBMs.forEach((element) => {
        ownerList.push(userInfoMap.get(element));
      });
      sroCommMgrs.forEach((element) => {
        approverList.push(userInfoMap.get(element));
      });
      setOwners(ownerList);
      setApprovers(approverList);
    }
  }, [userInfoMap]);

  function cancelStrategy(e) {
    e.preventDefault();
    if (!readOnly && isFormDirty()) setShowModal(true);
    else setMakeCancelAsLink(true);
  }

  async function deleteOKStrategy(e) {
    e.preventDefault();
    saveStrategy(getValues(), true);
  }

  async function checkDuplicateStrategies(mgcInfo, scopeInfo) {
    try {
      let mgcs = getNewMGCCriteria(mgcInfo);

      return checkStrategyDuplicateCombination(
        mgcs,
        [STRATEGY_STATUS_ACTIVE],
        scopeInfo.level,
        scopeInfo.unitGroups,
        scopeInfo.units,
        strategyNumber,
        commonProps
      );
    } catch (e) {
      // Ignore Error
      console.log(e);
      setStatusErrorMsg(
        "Couldn't validate for Duplicate strategies. Please try again!",
        e
      );
    }
  }

  function getApplicablePhase(strategyStatus) {
    if (strategyNumber === 0) return BEGINNING_PHASE;
    else
      return strategyStatus === STRATEGY_STATUS_ACTIVE
        ? STARTING_PHASE_FOR_ACTIVE_STATUS
        : strategyInformation.currentPhase;
  }

  function getPutOrPostScope(scopeLevel) {
    const scopeObj = getScope(scopeLevel);

    if (
      strategyNumber === 0 ||
      selectedScopeUnits ||
      isScopeAnyGlobalType(scopeLevel)
    )
      return scopeObj;
    else return selectedScopeViewList;
  }

  function putOrPostStrategy(data, strategyStatus, isSetupComplete) {
    let scopeLevel =
      getScopeLevel(data.scope) || selectedScopeViewList?.level || scopeType;
    let scope = getPutOrPostScope(scopeLevel);
    let mgcs = selectedMgcsUpdated
      ? checkedMgcs(
          selectedMgc.minorSegments,
          selectedMgc.commodity.value,
          selectedMgc.major.value
        )
      : selectedMgcOnLoad;

    let commonPayLoad = {
      currentPhase: getApplicablePhase(strategyStatus),
      ownerId: data.ownerId
        ? data.ownerId.value
        : selectedOverviewViewList.ownerId.value,
      approverId: data.approverId
        ? data.approverId.value
        : selectedOverviewViewList.approverId.value,
      scope: scope,
      mgcs: mgcs,
      status: strategyStatus,
      nonCurrentProspectiveSuppliers: [],
      currentProspectiveSuppliers: [],
      isSetupComplete
    };

    let method;
    let body;
    let url = getEnvVar('VITE_GSS_API_URL');

    if (strategyNumber !== 0) {
      url += '/' + strategyNumber;
      method = 'PUT';
      body = {
        ...commonPayLoad,
        strategyId: strategyNumber,
        suppliers: getSupplierJSON(selectedSuppliers),
        nonStrategySupplierList: nonStrategicSuppliers || []
      };
    } else {
      method = 'POST';
      body = {
        ...commonPayLoad
      };
    }
    return { url: url, method: method, body: body };
  }

  function isFormDirty() {
    return isDirty || mgcDirty || scopeDirty || suppliersDirty;
  }

  useBeforeUnload(
    (event) => {
      event.preventDefault();
      event.returnValue = '';
    },
    { when: isFormDirty() }
  );

  usePrompt(showDeleteModal !== null && !showModal && isFormDirty());

  function loadForm(data) {
    let obj = getUserObj(userInfoMap?.get(data.ownerId), data.ownerId);
    let object = getUserObj(userInfoMap?.get(data.approverId), data.approverId);

    // Clear all dirty flags
    clearDirty();
    setOwnerObj(obj);
    setStatusState(data.status);
    setLastUpdatedBy(
      getFormattedUserNameWithId(
        userInfoMap?.get(data.updatedById),
        data.updatedById
      )
    );
    setSelectedMgcList(prepareMGCData(data.mgcs));
    setSelectedOverviewViewList({
      ownerId: obj,
      approverId: object,
      strategyDesc: data.strategyDesc
    });
    const nextSelectedScopeViewList = {
      level: data.scope.level,
      unitGroups: data.scope.unitGroups,
      units: data.scope.units
    };
    dispatch(
      hydrateScope({
        scopeType: data.scope?.level,
        selectedScopeViewList: nextSelectedScopeViewList,
        selectedScopeUnits: null
      })
    );
    setSelectedMgcOnLoad(prepareSelectedMGCData(data.mgcs));
    setSelectedMgcAfterEdit(selectedMgc);
    setSelectedMgcsUpdated(false);
  }

  function getScope(scopeLevel, tmpSelectedScopeUnits) {
    let { newSelectedParent, newSelectedChild } = checkedScope(
      tmpSelectedScopeUnits || selectedScopeUnits,
      scopeLevel
    );
    return {
      level: scopeLevel,
      unitGroups: newSelectedParent,
      units: newSelectedChild
    };
  }

  function getScopeLevel(scopeValue) {
    if (typeof scopeValue === 'string') {
      return scopeValue;
    }
    return scopeValue?.level;
  }

  function getSupplierJSON(splrs) {
    return splrs
      ? splrs.map((supplier) => {
          return {
            supplierNumber: supplier.supplierNumber,
            status: supplier.statusDesc || supplier.status,
            comments: supplier.comments
          };
        })
      : [];
  }

  function validate(data) {
    let error = false;

    function checkMgcError() {
      if (
        (selectedMgcsUpdated || !isEdit) &&
        (!selectedMgc?.commodity || !selectedMgc.major)
      ) {
        error = true;
        setSaveMgcError(true);
      }
    }

    function checkScopeError() {
      let scopeItem =
        getScopeLevel(data.scope) || selectedScopeViewList?.level || scopeType;

      if (scopeItem) {
        let scope = selectedScopeUnits
          ? getScope(scopeItem, selectedScopeUnits)
          : selectedScopeViewList;

        if (isScopeAnyGlobalType(scopeItem)) {
          dispatch(setScopeInvalidErrorInState(null));
        } else if (
          scope.level === SCOPE_LEVEL_UNITORUNITGROUP &&
          scope.unitGroups.length === 0 &&
          scope.units.length === 0
        ) {
          error = true;
          dispatch(
            setScopeInvalidErrorInState('At least 1 Unit must be selected')
          );
          if (strategyInformation) {
            dispatch(
              setSelectedScopeViewListInState({
                level: scopeItem,
                unitGroups: [],
                units: []
              })
            );
          }
        }
      } else {
        error = true;
        setMissingScopeError(true);
      }
    }

    checkMgcError();
    checkScopeError();

    return error;
  }

  function clearDirtyForPageReload() {
    reset(getValues(), {
      keepErrors: false,
      keepDirty: false,
      keepIsSubmitted: false,
      keepTouched: false,
      keepIsValid: false,
      keepSubmitCount: false
    });
    clearDirty();
  }

  function handleDeleteSuccess() {
    clearDirtyForPageReload();
    setShowDeleteModal(null);
    setMakeCancelAsLink(true);
    enqueueToast({
      variant: 'success',
      message: String.raw`Strategy #${strategyNumber} was successfully deleted.`
    });
  }
  function handleErrorResponse(response) {
    if (response.meta.status >= 400) {
      const result = response.json;

      setStatusErrorMsg(result[response.meta.status]);
    }
  }
  function callSaveStrategy(putOrPost, deleteOperation, submitButton) {
    incrementAPIInProgress();
    fetchJsonWithAuth(putOrPost.url, {
      method: putOrPost.method,
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      },
      body: JSON.stringify(putOrPost.body)
    })
      .then((response) => {
        const result = response.json;
        const { strategyId } = result;

        if (!strategyId) handleErrorResponse(response);

        if (deleteOperation) handleDeleteSuccess();
        else {
          clearDirtyForPageReload();

          if (putOrPost.method === 'POST') {
            setStrategyNumber(strategyId);

            enqueueToast({
              variant: 'success',
              message: String.raw`Strategy# ${strategyId} has been successfully created.`
            });
            // Replace the location
            if (submitButton === 'complete') {
              navigate(
                {
                  pathname: `/editStrategy/${strategyId}`,
                  state: {
                    strategy: result
                  }
                },
                { replace: true }
              );
            } else {
              navigate(
                {
                  pathname: `/createStrategy/${strategyId}`,
                  state: {
                    strategy: result
                  }
                },
                { replace: true }
              );
            }
          } else if (putOrPost.method === 'PUT') {
            setStrategySave({
              variant: 'success',
              message: String.raw`Strategy# ${strategyId} has been successfully updated.`
            });

            if (submitButton === 'complete') {
              navigate(
                {
                  pathname: `/editStrategy/${strategyId}`,
                  state: {
                    strategy: result
                  }
                },
                { replace: true }
              );
            }
          }

          if (submitButton === 'saveNext') {
            handleNext();
          }
        }
      })
      .catch((error) =>
        setStatusErrorMsg(
          "Couldn't save the strategy. Please try again.",
          error
        )
      )
      .finally(() => decrementAPIInProgress());
  }

  // Change Warnings to Error if in Review Step
  useEffect(() => {
    if (
      activeStep === getNextStepNumberWrtSDFStep(STEPS.REVIEW) &&
      (duplicateStrategyList?.length > 0 ||
        (hasSDFStep && strategyInformation?.sdsFormId <= 0))
    )
      setOkToPublish(false);
  }, [activeStep, duplicateStrategyList]);

  useEffect(() => {
    if (strategySave) {
      enqueueToast(strategySave);
      setRefresh(true);
    }
  }, [strategySave]);

  async function saveStrategy(data, deleteOperation) {
    let submitButton = document.activeElement.value;
    let putOrPost;

    if (deleteOperation) {
      putOrPost = putOrPostStrategy(data, STRATEGY_STATUS_DELETED, false);
    } else if (submitButton === 'complete') {
      putOrPost = putOrPostStrategy(data, STRATEGY_STATUS_DRAFT, true);
    } else {
      setOkToPublish(true);
      putOrPost = putOrPostStrategy(data, STRATEGY_STATUS_DRAFT, false);
    }

    callSaveStrategy(putOrPost, deleteOperation, submitButton);
  }

  function handleScopeChangesForSDFStep(scopeType, selectedUnits) {
    let tmpHasSDFStep = handleSDFStepState(getScope(scopeType, selectedUnits));

    if (tmpHasSDFStep !== hasSDFStep && activeStep > STEPS.SDF) {
      if (hasSDFStep) {
        // Removing SDF Step
        setActiveStep(activeStep - 1);
      } else {
        // Adding SDF Step
        setActiveStep(STEPS.SDF);
      }
    }
  }
  function handleSDFStepState(scopeTypeObj) {
    // SDF Check
    let hasUsIndicator = findUSIndicatorNew(scopeTypeObj, scopeUSIndicatorMap);
    let hasSDFStep = hasUsIndicator || strategyInformation?.sdsFormId > 0;

    setHasUSScope(hasUsIndicator);
    setHasSDFStep(hasSDFStep);
    return hasSDFStep;
  }

  function clearDirty() {
    setMgcDirty(false);
    dispatch(setScopeDirtyInState(false));
    setSuppliersDirty(false);
    setStrategySave(null);
  }

  function prepareMGCData(mgcsFromStrategy) {
    let commodity = [];
    let major = [];
    let minorList = [];
    let segmentList = [];

    commodity.push(mgcsFromStrategy[0].commodity);
    major.push(mgcsFromStrategy[0].major);
    mgcsFromStrategy.forEach((record) => {
      if (record.segment) segmentList.push(record.mgccode);
      else if (record.minor) minorList.push(record.mgccode);
    });

    return {
      commodities: commodity,
      majors: major,
      parent: minorList,
      children: segmentList
    };
  }

  function prepareSelectedMGCData(mgcsFromStrategy) {
    let data = [];

    mgcsFromStrategy.forEach((record) => {
      let row = {
        commodity: record.commodity,
        major: record.major,
        minor: record.minor,
        segment: record.segment
      };
      data.push(row);
    });

    return data;
  }

  useEffect(() => {
    if (strategyNumber > 0 && hasCreateAccess && tokenAvailable) {
      setIsEdit(true);

      callStrategiesGetAPI(
        strategyNumber,
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        setStrategyInformation,
        setSelectedSuppliers,
        setStrategySupplierUsers
      );
    }
  }, [strategyNumber, tokenAvailable, hasCreateAccess]);

  useEffect(() => {
    if (strategyInformation) {
      if (strategyInformation.status === STRATEGY_STATUS_ACTIVE)
        navigate(
          {
            pathname: `/editStrategy/${strategyNumber}`,
            state: {
              strategy: strategyInformation
            }
          },
          { replace: true }
        );
    }
  }, [strategyInformation]);

  useEffect(() => {
    if (strategyInformation && userInfoMap) loadForm(strategyInformation);
  }, [strategyInformation, userInfoMap, masterMGCJson]);

  useEffect(() => {
    const currentScope = selectedScopeUnits
      ? getScope(scopeType, selectedScopeUnits)
      : selectedScopeViewList;
    setSupplierScopeOverview(currentScope);
  }, [selectedScopeUnits, scopeType, selectedScopeViewList]);

  useScopeValidation({
    setValue,
    onScopeChange: (type, units) => {
      setMissingScopeError(!type);
      handleScopeChangesForSDFStep(type, units);
    }
  });

  useEffect(() => {
    if (refresh && strategyNumber > 0 && hasCreateAccess && tokenAvailable) {
      callStrategiesGetAPI(
        strategyNumber,
        incrementAPIInProgress,
        decrementAPIInProgress,
        setStatusErrorMsg,
        setStrategyInformation,
        setSelectedSuppliers,
        setStrategySupplierUsers
      );
      setRefresh(false);
    }
  }, [strategyNumber, refresh, tokenAvailable, hasCreateAccess]);

  function findErrorStep(duplicateStrategyCount, strategyInfo, hasSDFStep) {
    let errorStepLink;

    if (duplicateStrategyCount > 0) errorStepLink = STEPS_LINK.DEFINE;
    else if (hasSDFStep && !strategyInfo.sdsFormId)
      errorStepLink = STEPS_LINK.SDF;

    return errorStepLink;
  }
  function scrollToLink(link) {
    link && document.querySelector(`#${link}`)?.scrollIntoView();
  }
  function findAndSetNextStep(
    duplicateStrategyCount,
    strategyInfo,
    hasSDFStep
  ) {
    let submitButton = document.activeElement.value;

    if (activeStep === 0 && submitButton !== 'save') {
      let nextStep = STEPS.SUPPLIER;
      let stepLink = STEPS_LINK.SUPPLIER;
      let errorStepLink = findErrorStep(
        duplicateStrategyCount,
        strategyInfo,
        hasSDFStep
      );

      if (strategyInfo.sdsFormId > 0) {
        nextStep = getNextStepNumberWrtSDFStep(STEPS.REVIEW, hasSDFStep);
        stepLink = STEPS_LINK.REVIEW;
      } else if (strategyInfo.suppliers?.length > 0) {
        nextStep = hasSDFStep
          ? STEPS.SDF
          : getNextStepNumberWrtSDFStep(STEPS.REVIEW, hasSDFStep);
        stepLink = hasSDFStep ? STEPS_LINK.SDF : STEPS_LINK.REVIEW;
      }

      setActiveStep(nextStep);

      // Scroll to the Step Link
      scrollToLink(errorStepLink || stepLink);
    }
  }
  useEffect(() => {
    const handleStepCalc = async () => {
      let dupList;
      let hasUsIndicator = false;

      if (STRATEGY_STATUS_ACTIVE !== strategyInformation.status) {
        dupList = await checkDuplicateStrategies(
          strategyInformation.mgcs,
          strategyInformation.scope
        );

        setDuplicateStrategyList(dupList);
      }

      // SDF Handler
      if (strategyInformation.scope)
        hasUsIndicator = handleSDFStepState(strategyInformation.scope);

      // Set Next Step
      findAndSetNextStep(
        dupList ? dupList.length : 0,
        strategyInformation,
        hasUsIndicator
      );
    };

    if (strategyInformation && scopeUSIndicatorMap) {
      const currentUser = loggedInUser?.toUpperCase();
      // Require a non-empty logged-in user so undefined === undefined
      // can never grant access; admin override is preserved.
      const isOwnerOrApprover =
        !!currentUser &&
        (strategyInformation.ownerId?.toUpperCase() === currentUser ||
          strategyInformation.approverId?.toUpperCase() === currentUser);

      setHasAccess(isOwnerOrApprover || isAdmin);

      handleStepCalc();
    }
  }, [strategyInformation, scopeUSIndicatorMap]);

  function checkAndSave(data) {
    clearAllMessages();

    let error = validate(data);

    if (!error) saveStrategy(data);
  }

  function deleteStrategy(e) {
    e.preventDefault();
    setShowDeleteModal(true);
  }

  function setParentMgcData(mgcData, event) {
    setSelectedMgcsUpdated(true);
    setSelectedMgc(mgcData);
    event && setMgcDirty(true);
  }

  function resetSelectedMgcAfterEdit(selectedMgcTemp) {
    setSelectedMgcAfterEdit(selectedMgcTemp);
  }

  function setParentSupplierData(supplierData) {
    setSuppliersDirty(true);
    setSelectedSuppliers(supplierData);
  }

  function setParentNonStrategicSupplierData(supplierData, isNonUserUpdate) {
    // Data loads/fetch failures/enrichment refreshes shouldn't count as a user edit.
    if (!isNonUserUpdate) setSuppliersDirty(true);
    setNonStrategicSuppliers(supplierData);
  }

  useEffect(() => {
    window.addEventListener('message', receiveMessage, false);
  }, []);

  function receiveMessage(event) {
    if (isEventSourceTrusted(event)) {
      let message = event.data;

      if (message) {
        window.location.reload();
      }
    }
  }

  function getNextStepNumberWrtSDFStep(currentStep, tmpHasSDFStep) {
    return hasSDFStep || tmpHasSDFStep ? currentStep + 1 : currentStep;
  }

  let reviewStepNumber = getNextStepNumberWrtSDFStep(STEPS.REVIEW);

  function canNavigateToStep(targetStep) {
    return targetStep <= activeStep;
  }

  function goToStep(targetStep) {
    if (canNavigateToStep(targetStep)) {
      setActiveStep(targetStep);
    }
  }

  function handlePainting() {
    if (!info) return <></>;

    if (makeCancelAsLink) return <Navigate to="/" />;

    if (!hasCreateAccess || (isEdit && hasAccess === false))
      return <NoAccessEmployee userId={loggedInUser} />;
    else {
      return strategyNumber !== 0 &&
        !strategyInformation &&
        statusMessages?.error ? (
        <MessageBanner
          statusMessages={statusMessages}
          apiInProgressCount={apiInProgressCount}
        />
      ) : null;
    }
  }

  return (
    handlePainting() || (
      <>
        <PageHeader>
          <PageHeaderTitle>
            <Typography variant="h1">
              {strategyNumber > 0
                ? `Create Strategy #${strategyNumber}`
                : 'Create Strategy'}
            </Typography>
            {strategyNumber > 0 && strategyInformation && lastUpdatedBy && (
              <Typography color="textSecondary">
                Last updated by {lastUpdatedBy}
              </Typography>
            )}
          </PageHeaderTitle>
          <PageHeaderActions>
            {isEdit && strategyInformation && (
              <Button
                variant="tertiary"
                startIcon={<fuel-icon name="delete" />}
                onClick={deleteStrategy}
              >
                {LABELS.DELETE}
              </Button>
            )}
            <Button variant="secondary" onClick={cancelStrategy}>
              {LABELS.CANCEL}
            </Button>
            <Button
              variant="secondary"
              type="submit"
              value="save"
              form="strategyForm"
            >
              {LABELS.SAVE_DRAFT}
            </Button>
            <Button
              variant="primary"
              type="submit"
              value="complete"
              form="strategyForm"
            >
              {LABELS.COMPLETE_SETUP}
            </Button>
          </PageHeaderActions>
        </PageHeader>
        {/* Grid items default to min-width:auto, so without this the page-content track is sized by its content and any wide child widens the page. */}
        <PageContent sx={{ minWidth: 0 }}>
          <Box
            component="form"
            id="strategyForm"
            onSubmit={handleSubmit(checkAndSave)}
          >
            <MessageBanner
              statusMessages={statusMessages}
              apiInProgressCount={apiInProgressCount}
            />
            <div>
              {statusMessages?.success && (
                <MessageBanner
                  statusMessages={statusMessages}
                  apiInProgressCount={apiInProgressCount}
                />
              )}
            </div>
            <Box>
              <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
                <Step
                  expanded={activeStep >= STEPS.DEFINE}
                  completed={activeStep > STEPS.DEFINE}
                >
                  <StepButton
                    onClick={() => goToStep(STEPS.DEFINE)}
                    disabled={!canNavigateToStep(STEPS.DEFINE)}
                    optional={
                      <Box component="span" sx={{ typography: 'bodyMedium' }}>
                        Select the MGCs and using units covered by your
                        strategy. Choose multiple regions or specific units only
                        when appropriate, as this can lead to gaps in strategy
                        coverage.
                      </Box>
                    }
                  >
                    <Typography variant={'h2'}>Define Strategy</Typography>
                  </StepButton>
                  <StepContent sx={{ width: '100%' }}>
                    <DefineStrategy
                      showSaveButton={activeStep === STEPS.DEFINE}
                      commonProps={commonProps}
                      strategyProps={strategyProps}
                      strategyInformation={strategyInformation}
                    />
                  </StepContent>
                </Step>

                <Step
                  expanded={activeStep >= STEPS.SUPPLIER}
                  completed={activeStep > STEPS.SUPPLIER}
                >
                  <StepButton
                    onClick={() => goToStep(STEPS.SUPPLIER)}
                    disabled={!canNavigateToStep(STEPS.SUPPLIER)}
                    optional={
                      <Box component="span" sx={{ typography: 'bodyMedium' }}>
                        Indicate strategic suppliers for your strategy and their
                        appropriate status. All suppliers with spend for the
                        MGC/units selected will display in the Non-Strategic tab
                        initially and can be moved to the Strategic tab as
                        applicable.
                      </Box>
                    }
                  >
                    <Typography variant={'h2'}>Add Supplier</Typography>
                  </StepButton>
                  <StepContent sx={{ width: '100%' }}>
                    <AddSupplierStep
                      showSaveButton={activeStep === STEPS.SUPPLIER}
                      commonProps={commonProps}
                      strategyProps={strategyProps}
                      strategyInformation={strategyInformation}
                    />
                  </StepContent>
                </Step>

                {hasSDFStep && (
                  <Step
                    expanded={activeStep >= STEPS.SDF}
                    completed={activeStep > STEPS.SDF}
                  >
                    <StepButton
                      onClick={() => goToStep(STEPS.SDF)}
                      disabled={!canNavigateToStep(STEPS.SDF)}
                      optional={
                        <Box component="span" sx={{ typography: 'bodyMedium' }}>
                          {SBF_US_MESSAGE}
                        </Box>
                      }
                    >
                      <Typography variant={'h2'}>
                        {SMALL_BUSINESS_FORM}
                      </Typography>
                    </StepButton>
                    <StepContent sx={{ width: '100%' }}>
                      <SourcingDiversityFormStep
                        showSaveButton={activeStep === STEPS.SDF}
                        commonProps={commonProps}
                        strategyProps={strategyProps}
                        strategyInformation={strategyInformation}
                      />
                    </StepContent>
                  </Step>
                )}

                <Step
                  expanded={activeStep >= reviewStepNumber}
                  completed={activeStep > reviewStepNumber}
                >
                  <StepButton
                    onClick={() => goToStep(reviewStepNumber)}
                    disabled={!canNavigateToStep(reviewStepNumber)}
                    optional={
                      <Box component="span" sx={{ typography: 'bodyMedium' }}>
                        Complete strategy set up prior to moving to the Maintain
                        Strategy page. You will complete the strategy completion
                        process on the Maintain Strategy page. A strategy will
                        not move to Active Status until it is published.
                      </Box>
                    }
                  >
                    <Typography variant={'h2'}>
                      {LABELS.REVIEW_STRATEGY}
                    </Typography>
                  </StepButton>
                  <StepContent sx={{ width: '100%' }}>
                    <ReviewPublish
                      showSaveButton={activeStep === reviewStepNumber}
                      commonProps={commonProps}
                      strategyProps={strategyProps}
                      duplicateStrategyCount={duplicateStrategyList?.length}
                      hasSDFStep={hasSDFStep}
                      isSDFError={
                        hasSDFStep && strategyInformation?.sdsFormId <= 0
                      }
                    />
                  </StepContent>
                </Step>
              </Stepper>
            </Box>
          </Box>
        </PageContent>

        <SMModal
          showModal={showModal}
          handleCancel={handleModalCancel}
          handleYes={cancelOKStrategy}
          title="Changes Will Be Lost"
          msg_line1="Do you want to save your changes before leaving this screen?"
          msg_line2="If you don't save, your changes will be lost."
          cancelText="Cancel"
          yesText="Continue without Saving"
        />
        <SMModal
          showModal={showDeleteModal}
          handleCancel={handleModalDeleteCancel}
          handleYes={deleteOKStrategy}
          title="Delete Strategy?"
          msg_line1="Are you sure you want to delete this strategy?"
          cancelText="Cancel"
          yesText="Delete Strategy"
        />
      </>
    )
  );
}
